import * as React from 'react'
import { ReactElement, useCallback, useEffect, useMemo } from 'react'
import { Button, DialogActions, DialogBody, DialogContent, DialogTitle, DialogTrigger } from '@fluentui/react-components'
import FormContainer from '../../../../components/Form/FormContainer'
import { DriveItem } from '@microsoft/microsoft-graph-types'
import useAxios from '../../../../hooks/useAxios'
import { useTranslation } from 'react-i18next'
import useViewport, { Viewport } from '../../../../hooks/useViewport'
import { DocumentDto } from '../../../../context/GlobalContext'
import { useFormikContext } from 'formik'
import { useSettings } from '../../../../hooks/useSettings'
import { HammockFormDialogProps } from '../HammockFormDialog'
import { DocumentSelectionType } from '../../../../components/Dropdown/DocumentDropdown'
import { DocumentContext, DocumentIconNameMap, FolderType } from '../../../../config/documentConfig'
import { Destination } from '../../../../context/AxiosContext'
import { FilePartyDto } from '../../../../config/filePartyConfig'
import { driveItemListProperties } from '../../../../helpers/documentHelper'
import useDocumentsOpen from '../../../../hooks/document/useDocumentsOpen'
import { findDefaultFolderPaths } from '../../../../helpers/folderSettingsHelper'
import { formatInputDocumentName, formatTemplateDocumentName } from '../../../../helpers/regexHelper'
import useSubmitDocumentGenerationRequest from '../../../../hooks/finance/report/useSubmitDocumentGenerationRequest'
import { DocumentGenerationStatusData, DocumentGenerationType } from '../../../../hooks/document/documentGenerationTypes'
import { QueueStatus } from '../../../../hooks/queue/queueTypes'
import { stealthClick } from '../../../../components/StealthLink'
import * as Yup from 'yup'
import DocumentCombobox, { DocumentComboboxProps } from '../../Combobox/DocumentCombobox'
import FormikComboboxField from '../../../../components/Formik/FormikComboboxField'
import { FormikConfig } from 'formik/dist/types'
import EnumMapCombobox, { EnumMapComboboxProps } from '../../../../components/Combobox/EnumMapCombobox'
import { FolderDriveItem } from '../../../../components/Document/DocumentFlatTree'
import FormikInputField from '../../../../components/Formik/FormikInputField'
import { IFileDto } from '../../../../config/fileConfig'

export const getOfficeDocumentDialogBodySchema = (translate: (key: string) => string): Yup.ObjectSchema<any> => {
    return Yup.object().shape({
        type: Yup.string().required(translate('file.pivot.document.create.template.required')),
        template: Yup.string().required(translate('file.pivot.document.create.template.required')),
        fileId: Yup.string().required(translate('file.pivot.document.create.template.required')),
        folderId: Yup.string().required(translate('file.pivot.document.create.folderid.required')),
        namePrefix: Yup.string()
            .required(translate('file.pivot.document.create.name.required'))
            .test('is-valid-name', translate('file.pivot.document.create.name.invalid'), value => !value.match(/^\./)),
    })
}

export type OfficeDocumentDialogBodyProps = {
    context: DocumentContext
    folders?: FolderDriveItem[]
    // Called with the folder tree the combobox lazy-loaded (when no `folders` were supplied), so a
    // caller can persist it (e.g. into the document cache in FileContext) and reuse it on later opens.
    onFoldersLoaded?: (items: FolderDriveItem[]) => void
    parentId?: string //selected folder
    documentRootId?: string //root folder to use to lazy load the document tree
    file?: IFileDto
    party?: FilePartyDto
    callback?: (driveItem: DriveItem) => void
}

export type OfficeDocumentDialogBodyValues = {
    userId: string
    template: string
    fileId: string
    folderId: string
    name: string
    namePrefix?: string
    nameSuffix?: string
    pivotId?: string
    // HAM-4167: absent means the Azure template mount; for SHAREPOINT the uri is the path within the drive.
    storage?: 'AZURE' | 'SHAREPOINT'
    driveId?: string
}

export default function OfficeDocumentDialogBody(
    props: HammockFormDialogProps<OfficeDocumentDialogBodyValues, OfficeDocumentDialogBodyProps>,
): ReactElement {
    const axios = useAxios()
    const { settings } = useSettings()
    const documentsOpen = useDocumentsOpen()
    const { t: translate } = useTranslation()
    const { viewport, isViewport } = useViewport()

    const { values, setFieldValue, setFieldError } = useFormikContext<OfficeDocumentDialogBodyValues>()

    const dialogFormBodyProps = useMemo(() => props.dialogFormBodyProps() ?? ({} as OfficeDocumentDialogBodyProps), [props])
    const viewportSmall = useMemo<boolean>(() => isViewport('<', Viewport.MainMenu), [viewport])

    const templateOptions = useMemo<Map<DocumentDto, string>>(
        () =>
            new Map(
                (settings?.documents || [])
                    .filter(template => !template.context || template.context === dialogFormBodyProps.context)
                    .filter(template => !template.role || !dialogFormBodyProps.party || template.role === dialogFormBodyProps.party?.role)
                    .map(template => [template, template.label] as [DocumentDto, string]),
            ),
        [settings, settings?.documents],
    )
    const folders = useMemo(() => dialogFormBodyProps?.folders?.filter(item => item.itemType === 'branch'), [dialogFormBodyProps.folders])

    // Child folders of a folder: from the loaded flat tree when present, otherwise fetched on demand.
    const getChildFolders = useCallback(
        async (parentId: string): Promise<{ id: string; name: string }[]> => {
            const loaded = folders?.filter(folder => (folder.parentValue ?? dialogFormBodyProps.documentRootId) === parentId)
            if (loaded?.length) {
                return loaded.map(folder => ({ id: folder.id, name: folder.name }))
            }
            if (!parentId) {
                return []
            }
            const response = await axios.get<DriveItem>(`/items/${parentId}`, {
                baseURL: Destination.Documents,
                params: { $select: driveItemListProperties.join(','), $expand: 'children' },
                headers: { SuppressErrors: 'true' },
            })
            return (response.data.children || []).filter(child => child.folder).map(child => ({ id: child.id, name: child.name }))
        },
        [folders, axios, dialogFormBodyProps.documentRootId],
    )

    // Resolve the target folder for a folder type by walking each candidate settings chain
    // (enabled chains first) through the dossier tree, so the target may sit on a nested level.
    // Nothing is created — a chain that does not exist in the dossier simply does not match.
    const resolveTargetFolderId = useCallback(
        async (folderType: string): Promise<string> => {
            if (!folderType) {
                return undefined
            }
            for (const path of findDefaultFolderPaths(settings?.folders, folderType)) {
                let currentId: string = dialogFormBodyProps.documentRootId
                for (const segment of path) {
                    const search = segment.label.toLowerCase()
                    const match = (await getChildFolders(currentId)).find(child => child.name.toLowerCase().indexOf(search) !== -1)
                    currentId = match?.id
                    if (!currentId) {
                        break
                    }
                }
                if (currentId) {
                    return currentId
                }
            }
            return undefined
        },
        [settings?.folders, getChildFolders, dialogFormBodyProps.documentRootId],
    )

    useEffect(() => {
        if (values.namePrefix && values.nameSuffix) {
            setFieldValue('name', formatInputDocumentName(values.namePrefix, values.nameSuffix))
        } else {
            const template: DocumentDto = (templateOptions.keys() || []).find(template => template.uri === values.template)

            setFieldValue('namePrefix', values.namePrefix ?? '')
            setFieldValue('nameSuffix', values.nameSuffix ?? DocumentIconNameMap.get(template?.type) ?? '')
        }
    }, [values.namePrefix, values.nameSuffix])

    useEffect(() => {
        const template: DocumentDto = (templateOptions.keys() || []).find(template => template.uri === values.template)

        if (template) {
            setFieldValue('type', template.type)
            setFieldValue(
                'namePrefix',
                formatTemplateDocumentName(template.name, { file: dialogFormBodyProps.file, party: dialogFormBodyProps.party }),
            )
            setFieldValue('nameSuffix', DocumentIconNameMap.get(template.type))

            let cancelled = false
            resolveTargetFolderId(template.folder)
                .then(folderId => folderId ?? resolveTargetFolderId(FolderType.Correspondence))
                .then(folderId => {
                    if (!cancelled) {
                        setFieldValue('folderId', folderId)
                    }
                })

            setFieldValue('storage', template.storage)
            setFieldValue('driveId', template.driveId)

            return () => {
                cancelled = true
            }
        }
        return undefined
    }, [values.template, folders])

    const submitOfficeRequest = useSubmitDocumentGenerationRequest<OfficeDocumentDialogBodyValues, DocumentGenerationStatusData>(
        DocumentGenerationType.Office,
        {
            [QueueStatus.Queued]: () => props.onDismiss(),
            [QueueStatus.Finished]: data => {
                dialogFormBodyProps?.callback?.(data.status.driveItem)
                stealthClick({ id: data.status.driveItem.id, item: data.status.driveItem, documentsOpen: documentsOpen }, axios)
            },
        },
    )
    const submitOfficeV2Request = useSubmitDocumentGenerationRequest<OfficeDocumentDialogBodyValues, DocumentGenerationStatusData>(
        DocumentGenerationType.OfficeV2,
        {
            [QueueStatus.Queued]: () => props.onDismiss(),
            [QueueStatus.Finished]: data => {
                dialogFormBodyProps?.callback?.(data.status.driveItem)
                stealthClick({ id: data.status.driveItem.id, item: data.status.driveItem, documentsOpen: documentsOpen }, axios)
            },
        },
    )

    const handleSubmit = useCallback<FormikConfig<OfficeDocumentDialogBodyValues>['onSubmit']>(
        values => {
            const template = settings.documents.find(t => t.uri === values.template)
            if (template?.version === 2) {
                submitOfficeV2Request(values, template.label)
            } else if (template) {
                submitOfficeRequest(values, template.label)
            }
        },
        [settings.documents, submitOfficeV2Request, submitOfficeRequest],
    )

    useEffect(() => {
        props.setOnSubmit?.(handleSubmit)
    }, [handleSubmit])

    return (
        <DialogBody>
            <DialogTitle>{translate(`file.pivot.document.create.standard`)}</DialogTitle>
            <DialogContent>
                <FormContainer>
                    <FormikComboboxField<OfficeDocumentDialogBodyValues, EnumMapComboboxProps<DocumentDto>>
                        required
                        labelKey={'file.pivot.document.create.template.label'}
                        orientation={viewportSmall ? 'vertical' : 'horizontal'}
                        fieldName={'template'}
                        component={EnumMapCombobox}
                        componentProps={{
                            values: templateOptions,
                            placeholderPrefixKey: '',
                            placeholderKey: 'file.pivot.document.create.template.placeholder',
                            getEntityValue: entity => entity?.uri,
                        }}
                    />
                    <FormikComboboxField<OfficeDocumentDialogBodyValues, DocumentComboboxProps>
                        required
                        labelKey={'file.pivot.document.create.folderid.label'}
                        orientation={viewportSmall ? 'vertical' : 'horizontal'}
                        fieldName={'folderId'}
                        component={DocumentCombobox}
                        componentProps={{
                            selectionType: DocumentSelectionType.FolderOnly,
                            entities: folders,
                            documentRootId: dialogFormBodyProps.documentRootId,
                            referenceNumber: dialogFormBodyProps.file?.reference,
                            onLazyLoaded: items => {
                                dialogFormBodyProps.folders = items
                                dialogFormBodyProps.onFoldersLoaded?.(items)
                            },
                            // Surface combobox-load failures (missing documentRootId, 4xx, …)
                            // as a regular Formik field error so it renders under the input
                            // just like a validation message.
                            onError: message => setFieldError('folderId', message),
                        }}
                    />
                    <FormikInputField
                        required
                        labelKey={'file.pivot.document.create.name.label'}
                        orientation={viewportSmall ? 'vertical' : 'horizontal'}
                        fieldName={'namePrefix'}
                        componentProps={{
                            placeholder: translate('file.pivot.document.create.name.label'),
                            contentAfter: `.${values.nameSuffix}`,
                        }}
                    />
                </FormContainer>
            </DialogContent>
            <DialogActions>
                <Button appearance="primary" type={'submit'}>
                    {translate('action.create.document')}
                </Button>
                <DialogTrigger disableButtonEnhancement>
                    <Button appearance="secondary">{translate('action.cancel')}</Button>
                </DialogTrigger>
            </DialogActions>
        </DialogBody>
    )
}
